import { useEffect, useState } from "react";
import { api, getToken } from "../api";

/**
 * Fallback when SPA still catches /phpmyadmin (e.g. direct node :7001).
 * Preferred path: nginx serves real phpMyAdmin at /phpmyadmin/sso.php.
 *
 * - If ?token= present → redirect to real PMA signon
 * - If panel session exists → mint SSO and redirect
 * - Else → clear error with retry guidance
 */
export function AdminerPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Token present → hand off to real phpMyAdmin
        if (token) {
          window.location.replace(
            `/phpmyadmin/sso.php?token=${encodeURIComponent(token)}`
          );
          return;
        }

        // Already on /phpmyadmin/sso.php path should not hit SPA
        const panelToken = getToken();
        if (!panelToken) {
          if (!cancelled) {
            setError(
              "Missing SSO token. Open phpMyAdmin from WHM or cPanel (home → phpMyAdmin)."
            );
            setBusy(false);
          }
          return;
        }

        // Auto-mint server-wide for staff, else user-scoped
        const res = await api<{ url: string }>("/api/v1/databases/pma-sso", {
          method: "POST",
          body: JSON.stringify({ serverWide: true })
        }).catch(async () =>
          api<{ url: string }>("/api/v1/databases/pma-sso", {
            method: "POST",
            body: JSON.stringify({ serverWide: false })
          })
        );

        if (cancelled) return;
        const path = res.url.startsWith("http")
          ? res.url
          : `${window.location.origin}${res.url}`;
        window.location.replace(path);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Could not open phpMyAdmin. Try again from the panel."
          );
          setBusy(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f3f3f3",
        fontFamily: "system-ui, sans-serif",
        padding: 24
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: "100%",
          background: "#fff",
          border: "1px solid #ccc",
          borderRadius: 6,
          padding: "28px 24px",
          boxShadow: "0 2px 8px rgba(0,0,0,.06)"
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "#6c78af",
            marginBottom: 8
          }}
        >
          phpMyAdmin
        </div>
        {busy ? (
          <p style={{ color: "#555", margin: 0 }}>
            Opening real phpMyAdmin via secure SSO…
          </p>
        ) : (
          <>
            <p style={{ color: "#b00020", margin: "0 0 16px", fontSize: 14 }}>
              {error}
            </p>
            <a
              href="/"
              style={{
                display: "inline-block",
                background: "#6c78af",
                color: "#fff",
                textDecoration: "none",
                padding: "8px 14px",
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 600
              }}
            >
              Back to panel
            </a>
          </>
        )}
      </div>
    </div>
  );
}
